import React, { useState, useEffect } from 'react';
import { 
    Box, Container, Typography, Paper, Button, Chip, Divider, 
    CircularProgress, Alert, ToggleButtonGroup, ToggleButton,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    FormControlLabel, Switch, Backdrop
} from '@mui/material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../../services/api'; 
import { useAuth } from '../../context/AuthContext';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ArrowBackIcon from '@mui/icons-material/ArrowBack'; 
import AutorenewIcon from '@mui/icons-material/Autorenew';

const FunderBillingPage = () => {
    const { token, user } = useAuth(); 
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    
    const [billingInfo, setBillingInfo] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isVerifying, setIsVerifying] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false); 
    const [autoRenew, setAutoRenew] = useState(false);

    const upgradeTier = searchParams.get('upgradeTier');
    const invoiceAmount = searchParams.get('invoiceAmount');
    const isRenewing = searchParams.get('action') === 'renew';
    const [billingCycle, setBillingCycle] = useState(searchParams.get('cycle') === 'annual' ? 'annual' : 'monthly');

    // TRIPWIRE: This will print to your console to prove the new code is running!
    console.log("🚨 V5 BULLETPROOF BILLING UI LOADED 🚨");

    const fetchFreshData = async () => {
        // FIX 1: Stop the 401 crashes! Do not fire until the token is ready.
        if (!token) return; 
        
        try {
            const ts = new Date().getTime();
            const [compRes, histRes] = await Promise.all([
                api.get(`/company/my-company?_t=${ts}`, { headers: { Authorization: `Bearer ${token}` } }),
                api.get(`/billing/history?_t=${ts}`, { headers: { Authorization: `Bearer ${token}` } })
            ]);
            setBillingInfo(compRes.data);
            setAutoRenew(compRes.data.AutoRenew === 1);
            setHistory(histRes.data);
        } catch (err) {
            console.error("Failed to load billing data", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (token) fetchFreshData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const handleYocoPayment = async () => {
        setIsVerifying(true);
        try {
            // 1. Ask the backend to generate the Yoco session
            const res = await api.post('/billing/create-yoco-checkout', { 
                newTier: targetTier,
                cycle: billingCycle,
                amountInCents: Math.round(finalAmount * 100),
                calculatedExpiry: isProRata ? currentExpiryDate.toISOString() : null, 
                isRenewal: isRenewing
            }, { headers: { Authorization: `Bearer ${token}` } });
            
            // 2. Save the ID locally so we can verify it when the user returns
            localStorage.setItem('yoco_checkout_id', res.data.checkoutId);
            
            // 3. Redirect the user to Yoco's secure hosted page!
            window.location.href = res.data.redirectUrl;
        } catch (error) {
            alert("Failed to connect to Yoco. Please try again.");
            setIsVerifying(false);
        }
    };

    useEffect(() => {
        const paymentStatus = searchParams.get('payment');
        const pendingCheckoutId = localStorage.getItem('yoco_checkout_id');

        // If the user just got back from a successful Yoco payment...
        if (paymentStatus === 'success' && pendingCheckoutId && token) {
            const finalizePayment = async () => {
                setIsVerifying(true);
                try {
                    // Tell the backend to verify the payment and upgrade the tier
                    await api.post('/billing/verify-yoco-payment', { 
                        checkoutId: pendingCheckoutId,
                        newTier: searchParams.get('tier'),
                        cycle: searchParams.get('cycle'),
                        calculatedExpiry: searchParams.get('expiry')
                    }, { headers: { Authorization: `Bearer ${token}` } });
                    
                    // Clean up and refresh the UI
                    localStorage.removeItem('yoco_checkout_id');
                    setSearchParams({}); 
                    await fetchFreshData();
                    setShowSuccess(true);
                } catch (error) {
                    alert("Payment verification failed. Please contact support.");
                } finally {
                    setIsVerifying(false);
                }
            };
            finalizePayment();
            
        } else if (token) {
            // Standard page load
            fetchFreshData();
        }
    }, [token, searchParams]);

    const targetTier = upgradeTier || billingInfo?.SubscriptionTier || 'STARTER';
    const isAnnual = billingCycle === 'annual';
    const isUpgrading = upgradeTier && upgradeTier !== billingInfo?.SubscriptionTier;
    const currentExpiryDate = new Date(billingInfo?.SubscriptionExpiresAt || new Date());
    
    let baseZAR = 85; 
    if (invoiceAmount) {
        baseZAR = parseInt(invoiceAmount, 10);
    } else {
        if (targetTier === 'INCUBATOR') baseZAR = isAnnual ? 850 : 85; 
        else if (targetTier === 'STARTER') baseZAR = isAnnual ? 25000 : 2500;
        else if (targetTier === 'GROWTH') baseZAR = isAnnual ? 95000 : 9500;
        else if (targetTier === 'ENTERPRISE') baseZAR = isAnnual ? 150000 : 15000;
    }

    let finalAmount = baseZAR;
    let isProRata = false;
    let daysLeft = 0;

    if (isUpgrading && currentExpiryDate > new Date()) {
        isProRata = true;
        const diffTime = currentExpiryDate.getTime() - new Date().getTime();
        daysLeft = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        const dailyRate = baseZAR / (isAnnual ? 365 : 30);
        finalAmount = Math.max(Math.round(dailyRate * daysLeft), 50); 
    }

        const handleReturnToDashboard = () => {
        // This completely wipes the React cache and forces a fresh database read!
        window.location.href = '/funder/billing'; 
    };


    // If loading or token is missing, show spinner
    if (loading || !token) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;
    if (!billingInfo) return <Alert severity="error">Failed to load billing profile.</Alert>;

    const isTrial = billingInfo.SubscriptionStatus === 'Trial';
    const isExpired = currentExpiryDate < new Date();
    const expiryDateStr = currentExpiryDate.toLocaleDateString();

    if (showSuccess) {
        return (
            <Container maxWidth="sm" sx={{ mt: 10, textAlign: 'center' }}>
                <Paper sx={{ p: 6, borderRadius: 4, border: '2px solid #4caf50' }}>
                    <CheckCircleIcon color="success" sx={{ fontSize: 80, mb: 2 }} />
                    <Typography variant="h4" fontWeight="bold" gutterBottom>Payment Successful!</Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                        Your subscription has been securely updated and your receipt has been logged.
                    </Typography>
                    <Button variant="contained" size="large" fullWidth onClick={handleReturnToDashboard}>
                        Return to Dashboard
                    </Button>
                </Paper>
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{ mt: 6, mb: 8 }}>
            
            <Backdrop sx={{ color: '#fff', zIndex: 9999, flexDirection: 'column' }} open={isVerifying}>
                <CircularProgress color="inherit" sx={{ mb: 2 }} />
                <Typography variant="h6" fontWeight="bold">Verifying secure payment...</Typography>
            </Backdrop>

            <Typography variant="h4" fontWeight="bold" gutterBottom>Subscription Dashboard</Typography>
            
            <Paper sx={{ p: 4, borderRadius: 3, border: '1px solid #e0e0e0', mb: 4, position: 'relative', overflow: 'hidden' }}>
                <Box sx={{ position: 'absolute', top: 0, left: 0, width: '8px', height: '100%', bgcolor: isExpired ? 'error.main' : 'success.main' }} />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                        <Typography variant="h6" color="text.secondary">Current Plan</Typography>
                        <Typography variant="h3" fontWeight="bold" color="primary">{billingInfo.SubscriptionTier}</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                        <Chip icon={isExpired ? null : <CheckCircleIcon />} label={isExpired ? 'Account Suspended' : isTrial ? 'Trial Active' : 'Subscription Active'} color={isExpired ? 'error' : isTrial ? 'warning' : 'success'} sx={{ fontWeight: 'bold', mb: 1 }} />
                        <Typography variant="body2" color="text.secondary" display="block">
                            {isExpired ? 'Expired on: ' : 'Valid until: '} <strong>{expiryDateStr}</strong>
                        </Typography>
                    </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 3 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        {!isUpgrading && !isRenewing && !invoiceAmount && (
                            <>
                                <Button variant="contained" color="primary" onClick={() => setSearchParams({ action: 'renew' })}>Renew Subscription</Button>
                                <Button variant="outlined" color="primary" onClick={() => navigate('/pricing')}>Change or Upgrade Plan</Button>
                            </>
                        )}
                    </Box>
                </Box>
            </Paper>

            {(isUpgrading || isRenewing || isExpired || isTrial || invoiceAmount) && (
                <Paper sx={{ p: 4, borderRadius: 3, border: `2px solid #6a11cb`, mb: 4 }}>
                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                        {invoiceAmount ? 'Pay Outstanding Invoice' : isRenewing ? `Renew ${targetTier} Subscription` : isUpgrading ? `Confirm Upgrade to ${targetTier}` : 'Activate Your Subscription'}
                    </Typography>
                    <Divider sx={{ my: 2 }} />

                    {!invoiceAmount && (
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Select Billing Cycle</Typography>
                            <ToggleButtonGroup value={billingCycle} exclusive onChange={(e, v) => v && setBillingCycle(v)} color="primary" fullWidth>
                                <ToggleButton value="monthly" sx={{ fontWeight: 'bold' }}>Pay Monthly</ToggleButton>
                                <ToggleButton value="annual" sx={{ fontWeight: 'bold' }}>Pay Annually (Save)</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                    )}

                    {invoiceAmount ? (
                        <Alert severity="warning" sx={{ mb: 4 }}><Typography variant="subtitle2" fontWeight="bold">Pay-As-You-Go Invoice</Typography><Typography variant="body2">You are settling an outstanding usage invoice for your active students.</Typography></Alert>
                    ) : isProRata ? (
                        <Alert severity="info" sx={{ mb: 4 }}>
                            <Typography variant="subtitle2" fontWeight="bold">Upgrade Pro-Rata Applied</Typography>
                            <Typography variant="body2">You are upgrading mid-cycle. You are only paying the price difference for the remaining <strong>{daysLeft} days</strong> of your current cycle. <br/>Your billing anniversary ({expiryDateStr}) remains unchanged.</Typography>
                        </Alert>
                    ) : (
                        <Alert severity="success" sx={{ mb: 4 }}>
                            <Typography variant="subtitle2" fontWeight="bold">Full Cycle Renewal</Typography>
                            <Typography variant="body2">You are purchasing a full {billingCycle} cycle. This will be added directly to your existing expiry date.</Typography>
                        </Alert>
                    )}

                    <Box sx={{ bgcolor: 'grey.50', p: 3, borderRadius: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                        <Box>
                            <Typography variant="h4" fontWeight="bold">Total: R{finalAmount.toLocaleString()}</Typography>
                            <Typography variant="body2" color="text.secondary">Securely processed via Yoco.</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button variant="outlined" color="inherit" onClick={() => setSearchParams({})} disabled={isVerifying} startIcon={<ArrowBackIcon />} sx={{ fontWeight: 'bold' }}>Cancel</Button>
                            
                            <Button variant="contained" size="large" onClick={handleYocoPayment} disabled={isVerifying}>
                                Pay Now with Yoco
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            )}

            <Typography variant="h5" fontWeight="bold" sx={{ mt: 6, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}><ReceiptIcon /> Payment History</Typography>
            <TableContainer component={Paper} variant="outlined">
                <Table>
                    <TableHead sx={{ bgcolor: 'action.selected' }}> 
                        <TableRow>
                            <TableCell><strong>Date</strong></TableCell>
                            <TableCell><strong>Reference</strong></TableCell>
                            <TableCell><strong>Plan</strong></TableCell>
                            <TableCell align="right"><strong>Amount Paid</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {history.length === 0 ? <TableRow><TableCell colSpan={4} align="center">No previous payments found.</TableCell></TableRow> : history.map((row) => (
                            <TableRow key={row.id}>
                                <TableCell>{new Date(row.date).toLocaleDateString()}</TableCell>
                                <TableCell sx={{ fontFamily: 'monospace' }}>{row.reference}</TableCell>
                                <TableCell><Chip label={`${row.tier} (${row.cycle})`} size="small" /></TableCell>
                                <TableCell align="right" fontWeight="bold">R{row.amount}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Container>
    );
};

export default FunderBillingPage;